import {css, StyleSheet} from 'aphrodite';
import React, {Fragment, ReactNode, useState} from 'react';

import {GRAY_LIGHT} from 'ui-library/Colors';

export interface ConfigStepsEditorProps<T, E extends object> {
  allFormErrors: Array<E>;
  setFormErrorsAtIndex: (formErrors: E, index: number) => void;
  configSteps: Array<T>;
  setConfigStepAtIndex: (step: T, index: number) => void;
  description?: ReactNode;
  getEmptyConfigStep: () => T;
  canAddConfigStep: (nextStep: T) => boolean;
  isEmptyStep: (configStep: T) => boolean;
  renderFirstConfigStepFormSection: (args: {
    handleSetFirstConfigStep: (configStep: T) => void;
    handleAddConfigStep: (configStep: T) => void;
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
    formErrors: E;
    setFormErrors: (formErrors: E) => void;
  }) => ReactNode;
  renderConfigStepHeader: (args: {
    isCollapsed: boolean;
    setIsCollapsed: (isCollapsed: boolean) => void;
    isLastStep: boolean;
    configStep: T;
    handleRemoveConfigStep: () => void;
    formErrors: E;
  }) => ReactNode;
  renderConfigStepForm: (args: {
    isLastStep: boolean;
    configStep: T;
    handleUpdateConfigStep: (configStep: T) => void;
    formErrors: E | undefined;
  }) => ReactNode;
  renderAddConfigStepButton: (args: {
    disabled: boolean;
    configStep: T;
    setFormErrors: (formErrors: E) => void;
    handleAddConfigStep: (lookupStepGroup: T) => void;
  }) => ReactNode;
  renderConfigStepFormChildren?: (args: {
    isLastStep: boolean;
    configStep: T;
    handleUpdateConfigStep: (configStep: T) => void;
    formErrors: E;
  }) => ReactNode;
  children?: ReactNode;
}

export default function ConfigStepsEditor<T, E extends object>(
  props: ConfigStepsEditorProps<T, E>
): JSX.Element {
  const {configSteps, setConfigStepAtIndex} = props;
  // Needed to avoid a potential infinite hook loop with NotebookFirstLookupStepForm
  const handleSetFirstConfigStep = React.useCallback(
    (configStep) => {
      setConfigStepAtIndex(configStep, 0);
    },
    [setConfigStepAtIndex]
  );

  const lastStepIndex = configSteps.map((configStep) => !props.isEmptyStep(configStep)).lastIndexOf(true);

  const [collapsedStates, setCollapsedStates] = useState(
    configSteps.map((_configStep, i) => i < lastStepIndex)
  );

  const setCollapsedStateAtIndex = (collapsedState: boolean, index: number): void => {
    const newCollapsedStates = [...collapsedStates];
    newCollapsedStates[index] = collapsedState;
    setCollapsedStates(newCollapsedStates);
  };

  const collapseAllExceptIndex = (index: number): void => {
    const newCollapsedStates = configSteps.map((configStep, i) => i !== index);
    setCollapsedStates(newCollapsedStates);
  };

  return (
    <>
      {props.description && <div className="u-moderate-spacing-bottom">{props.description}</div>}
      {props.children}
      {props.renderFirstConfigStepFormSection({
        handleSetFirstConfigStep,
        handleAddConfigStep: (configStep) => {
          collapseAllExceptIndex(1);
          setConfigStepAtIndex(configStep, 1);
        },
        isCollapsed: collapsedStates[0],
        setIsCollapsed: (isCollapsed) => {
          setCollapsedStateAtIndex(isCollapsed, 0);
        },
        formErrors: props.allFormErrors[0],
        setFormErrors: (formErrors) => {
          props.setFormErrorsAtIndex(formErrors, 0);
        },
      })}

      <div className="u-small-spacing-top">
        {configSteps.map((configStep, stepIndex) => {
          if (stepIndex === 0) {
            return null;
          }
          const nextStep = configSteps[stepIndex + 1];
          if (props.isEmptyStep(configStep)) {
            return null;
          }
          const isCollapsed = collapsedStates[stepIndex];
          const setIsCollapsed = (isCollapsed: boolean): void => {
            setCollapsedStateAtIndex(isCollapsed, stepIndex);
          };
          const formErrors = props.allFormErrors[stepIndex];
          const setFormErrors = (formErrors: E): void => {
            props.setFormErrorsAtIndex(formErrors, stepIndex);
          };
          const isLastStep = stepIndex >= lastStepIndex;
          const handleUpdateConfigStep = (step: T): void => {
            setConfigStepAtIndex(step, stepIndex);
          };
          const handleRemoveConfigStep = (): void => {
            setCollapsedStateAtIndex(false, stepIndex - 1);
            setConfigStepAtIndex(props.getEmptyConfigStep(), stepIndex);
            props.setFormErrorsAtIndex({} as E, stepIndex);
          };
          const handleAddConfigStep = (step: T): void => {
            collapseAllExceptIndex(stepIndex + 1);
            setConfigStepAtIndex(step, stepIndex + 1);
          };
          return (
            <Fragment key={stepIndex}>
              <div className={css(styles.configStepFormDivider)} />
              {props.renderConfigStepHeader({
                isCollapsed,
                setIsCollapsed,
                isLastStep,
                configStep,
                handleRemoveConfigStep,
                formErrors,
              })}
              {isCollapsed ? null : (
                <>
                  <div className={css(styles.configStepFormContainer)}>
                    {props.renderConfigStepForm({
                      isLastStep,
                      configStep,
                      handleUpdateConfigStep,
                      formErrors: formErrors || {},
                    })}
                  </div>
                  {props.renderConfigStepFormChildren
                    ? props.renderConfigStepFormChildren({
                        isLastStep,
                        configStep,
                        handleUpdateConfigStep,
                        formErrors,
                      })
                    : null}
                  {isLastStep ? (
                    <div>
                      {props.renderAddConfigStepButton({
                        disabled: !props.canAddConfigStep(nextStep),
                        configStep,
                        setFormErrors,
                        handleAddConfigStep,
                      })}
                    </div>
                  ) : null}
                </>
              )}
            </Fragment>
          );
        })}
      </div>
    </>
  );
}

const styles = StyleSheet.create({
  configStepFormContainer: {
    marginTop: '20px',
    marginBottom: '20px',
  },
  configStepFormDivider: {
    borderTop: `1px solid ${GRAY_LIGHT}`,
    marginTop: '10px',
    marginBottom: '10px',
  },
});
